import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlarmClock,
  AlertTriangle,
  Banknote,
  BarChart3,
  Briefcase,
  Cake,
  Calendar,
  CalendarDays,
  CheckCircle2,
  CheckSquare,
  History,
  Inbox,
  PieChart,
  Scale,
  Target,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
  XCircle,
} from 'lucide-react';
import {
  ATIVIDADES_WIDGET_IDS,
  CLIENTS_WIDGET_IDS,
  FINANCIAL_WIDGET_IDS,
  WIDGET_IDS,
  type DashboardPageId,
  type WidgetId,
} from '@proadv/types';

import { ClientsOverviewCard } from './cards/clients-overview';
import { ProcessesOverviewCard } from './cards/processes-overview';
import { RecentClientsCard } from './cards/recent-clients';
import { UpcomingDeadlinesCard } from './cards/upcoming-deadlines';
import { FinancialOverviewCard } from './cards/financial-overview';
import { PendingTasksCard } from './cards/pending-tasks';
import { TodayAgendaCard } from './cards/today-agenda';
import { NewIntimationsCard } from './cards/new-intimations';
import { AgendaMonthCard } from './cards/agenda-month';

import { SummaryTotalWidget } from './clients-widgets/summary-total';
import { SummaryWithProcessWidget } from './clients-widgets/summary-with-process';
import { SummaryWithoutProcessWidget } from './clients-widgets/summary-without-process';
import { TopSourcesWidget } from './clients-widgets/top-sources';
import { AgeRangesWidget } from './clients-widgets/age-ranges';
import { TopProfessionsWidget } from './clients-widgets/top-professions';
import { BirthdaysWidget } from './clients-widgets/birthdays';

import { SummarySaldoWidget } from './financial-widgets/summary-saldo';
import { SummaryReceitaPrevistaWidget } from './financial-widgets/summary-receita-prevista';
import { SummaryDespesaPrevistaWidget } from './financial-widgets/summary-despesa-prevista';
import { SummaryPagamentosAtrasadosWidget } from './financial-widgets/summary-pagamentos-atrasados';
import { ChartReceitaDespesaWidget } from './financial-widgets/chart-receita-despesa';
import { ContasBancariasWidget } from './financial-widgets/contas-bancarias';
import { TopCategoriasReceitaWidget } from './financial-widgets/top-categorias-receita';
import { TopCategoriasDespesaWidget } from './financial-widgets/top-categorias-despesa';
import { RecentTransactionsWidget } from './financial-widgets/recent-transactions';

import { ActivityChartWidget } from './atividades-widgets/activity-chart';
import { ProductivityWidget } from './atividades-widgets/productivity-card';
import { TasksCalendarWidget } from './atividades-widgets/tasks-calendar';

export interface WidgetMeta {
  id: string;
  title: string;
  icon: LucideIcon;
  Component: ComponentType;
}

const HOME_REGISTRY: Record<WidgetId, WidgetMeta> = {
  clients: {
    id: 'clients',
    title: 'Clientes',
    icon: Users,
    Component: ClientsOverviewCard,
  },
  processes: {
    id: 'processes',
    title: 'Processos',
    icon: Scale,
    Component: ProcessesOverviewCard,
  },
  recent_clients: {
    id: 'recent_clients',
    title: 'Últimos clientes',
    icon: UserPlus,
    Component: RecentClientsCard,
  },
  upcoming_deadlines: {
    id: 'upcoming_deadlines',
    title: 'Prazos próximos',
    icon: AlarmClock,
    Component: UpcomingDeadlinesCard,
  },
  financial: {
    id: 'financial',
    title: 'Financeiro',
    icon: Wallet,
    Component: FinancialOverviewCard,
  },
  pending_tasks: {
    id: 'pending_tasks',
    title: 'Tarefas pendentes',
    icon: CheckSquare,
    Component: PendingTasksCard,
  },
  today_agenda: {
    id: 'today_agenda',
    title: 'Agenda de hoje',
    icon: Calendar,
    Component: TodayAgendaCard,
  },
  new_intimations: {
    id: 'new_intimations',
    title: 'Intimações novas',
    icon: Inbox,
    Component: NewIntimationsCard,
  },
  agenda_month: {
    id: 'agenda_month',
    title: 'Calendário',
    icon: CalendarDays,
    Component: AgendaMonthCard,
  },
};

const CLIENTS_REGISTRY: Record<string, WidgetMeta> = {
  summary_total: {
    id: 'summary_total',
    title: 'Contatos cadastrados',
    icon: TrendingUp,
    Component: SummaryTotalWidget,
  },
  summary_with_process: {
    id: 'summary_with_process',
    title: 'Com processo ativo',
    icon: CheckCircle2,
    Component: SummaryWithProcessWidget,
  },
  summary_without_process: {
    id: 'summary_without_process',
    title: 'Sem processo ativo',
    icon: XCircle,
    Component: SummaryWithoutProcessWidget,
  },
  top_sources: {
    id: 'top_sources',
    title: 'Origem dos contatos',
    icon: BarChart3,
    Component: TopSourcesWidget,
  },
  age_ranges: {
    id: 'age_ranges',
    title: 'Faixa etária',
    icon: PieChart,
    Component: AgeRangesWidget,
  },
  top_professions: {
    id: 'top_professions',
    title: 'Profissões',
    icon: Briefcase,
    Component: TopProfessionsWidget,
  },
  birthdays: {
    id: 'birthdays',
    title: 'Aniversariantes do mês',
    icon: Cake,
    Component: BirthdaysWidget,
  },
};

const FINANCIAL_REGISTRY: Record<string, WidgetMeta> = {
  summary_saldo: {
    id: 'summary_saldo',
    title: 'Saldo atual',
    icon: Wallet,
    Component: SummarySaldoWidget,
  },
  summary_receita_prevista: {
    id: 'summary_receita_prevista',
    title: 'Receita prevista',
    icon: TrendingUp,
    Component: SummaryReceitaPrevistaWidget,
  },
  summary_despesa_prevista: {
    id: 'summary_despesa_prevista',
    title: 'Despesa prevista',
    icon: TrendingDown,
    Component: SummaryDespesaPrevistaWidget,
  },
  summary_pagamentos_atrasados: {
    id: 'summary_pagamentos_atrasados',
    title: 'Pagamentos atrasados',
    icon: AlertTriangle,
    Component: SummaryPagamentosAtrasadosWidget,
  },
  chart_receita_despesa: {
    id: 'chart_receita_despesa',
    title: 'Receita vs Despesa',
    icon: BarChart3,
    Component: ChartReceitaDespesaWidget,
  },
  contas_bancarias: {
    id: 'contas_bancarias',
    title: 'Contas',
    icon: Banknote,
    Component: ContasBancariasWidget,
  },
  top_categorias_receita: {
    id: 'top_categorias_receita',
    title: 'Top categorias — Receita',
    icon: TrendingUp,
    Component: TopCategoriasReceitaWidget,
  },
  top_categorias_despesa: {
    id: 'top_categorias_despesa',
    title: 'Top categorias — Despesa',
    icon: TrendingDown,
    Component: TopCategoriasDespesaWidget,
  },
  recent_transactions: {
    id: 'recent_transactions',
    title: 'Lançamentos recentes',
    icon: History,
    Component: RecentTransactionsWidget,
  },
};

const ATIVIDADES_REGISTRY: Record<string, WidgetMeta> = {
  tasks_activity_chart: {
    id: 'tasks_activity_chart',
    title: 'Atividade',
    icon: BarChart3,
    Component: ActivityChartWidget,
  },
  tasks_productivity: {
    id: 'tasks_productivity',
    title: 'Produtividade',
    icon: Target,
    Component: ProductivityWidget,
  },
  tasks_calendar_count: {
    id: 'tasks_calendar_count',
    title: 'Calendário de tarefas',
    icon: Calendar,
    Component: TasksCalendarWidget,
  },
};

export const WIDGET_REGISTRIES: Record<
  DashboardPageId,
  Record<string, WidgetMeta>
> = {
  home: HOME_REGISTRY,
  clients: CLIENTS_REGISTRY,
  financial: FINANCIAL_REGISTRY,
  atividades: ATIVIDADES_REGISTRY,
};

export const WIDGET_IDS_BY_PAGE: Record<DashboardPageId, readonly string[]> = {
  home: WIDGET_IDS,
  clients: CLIENTS_WIDGET_IDS,
  financial: FINANCIAL_WIDGET_IDS,
  atividades: ATIVIDADES_WIDGET_IDS,
};

export function getWidgetRegistry(
  pageId: DashboardPageId,
): Record<string, WidgetMeta> {
  return WIDGET_REGISTRIES[pageId];
}

export function getWidgetMeta(
  pageId: DashboardPageId,
  id: string,
): WidgetMeta | null {
  return WIDGET_REGISTRIES[pageId][id] ?? null;
}

/**
 * Compat: a home tinha uma exportacao chamada `WIDGET_REGISTRY`. Mantenho
 * o alias apontando para o registry da home — qualquer codigo legado que
 * importe por este nome continua funcionando.
 */
export const WIDGET_REGISTRY = HOME_REGISTRY;
